import React, { useEffect, useState } from 'react';
import { Plus, Save, Trash2, Truck } from 'lucide-react';
import { toast } from 'react-toastify';
import { settingsAPI } from '../../config/settings-api';
import './Settings.css';
import './OrderSettings.css';

/** UI tiers: closed max-km bands (shown as "0 – 30 km"), last always "Above X km". */
const DEFAULT_TIERS = [
  { kind: 'upto', upToKm: 30, amount: 5 },
  { kind: 'upto', upToKm: 50, amount: 10 },
  { kind: 'above', amount: 15 },
];

const serialize = (minOrderAmount, tiers) =>
  JSON.stringify({
    minOrderAmount: Number(minOrderAmount) || 0,
    tiers: (tiers || []).map((row) => ({
      kind: row.kind,
      upToKm: row.kind === 'upto' ? Number(row.upToKm) || 0 : null,
      amount: Number(row.amount) || 0,
    })),
  });

/** Range start for a closed tier (0, then previous max + 1). */
const rangeStartKm = (tiers, index) => {
  if (index <= 0) return 0;
  for (let i = index - 1; i >= 0; i -= 1) {
    if (tiers[i]?.kind === 'upto' && Number.isFinite(Number(tiers[i].upToKm))) {
      return Number(tiers[i].upToKm) + 1;
    }
  }
  return 0;
};

/** API slabs (from/to) → UI tiers (max-km bands / above). */
const slabsToTiers = (slabs) => {
  if (!Array.isArray(slabs) || !slabs.length) return DEFAULT_TIERS;

  const sorted = [...slabs].sort(
    (a, b) => (Number(a.fromKm) || 0) - (Number(b.fromKm) || 0)
  );
  const tiers = [];

  sorted.forEach((row) => {
    const amount = Number(row.amount) || 0;
    const toKm = row.toKm;
    if (toKm === null || toKm === undefined || toKm === '') {
      tiers.push({ kind: 'above', amount });
    } else {
      tiers.push({ kind: 'upto', upToKm: Number(toKm) || 0, amount });
    }
  });

  // Ensure exactly one trailing "above" tier
  if (!tiers.length || tiers[tiers.length - 1].kind !== 'above') {
    tiers.push({ kind: 'above', amount: 0 });
  } else {
    // Collapse multiple open-ended into last only
    const closed = tiers.filter((t) => t.kind === 'upto');
    const lastAbove = tiers.filter((t) => t.kind === 'above').pop();
    return [...closed, lastAbove];
  }

  return tiers;
};

/** UI tiers → API fromKm/toKm slabs. */
const tiersToSlabs = (tiers) => {
  const list = Array.isArray(tiers) && tiers.length ? tiers : DEFAULT_TIERS;
  const slabs = [];
  let prevMax = -1;

  list.forEach((row) => {
    const amount = Number(row.amount) || 0;
    if (row.kind === 'upto') {
      const toKm = Number(row.upToKm);
      const fromKm = prevMax < 0 ? 0 : prevMax + 1;
      slabs.push({ fromKm, toKm, amount });
      prevMax = toKm;
    } else {
      const fromKm = prevMax < 0 ? 0 : prevMax + 1;
      slabs.push({ fromKm, toKm: null, amount });
    }
  });

  return slabs;
};

const aboveThresholdLabel = (tiers, index) => {
  const prev = tiers[index - 1];
  if (prev?.kind === 'upto' && Number.isFinite(Number(prev.upToKm))) {
    return `Above ${Number(prev.upToKm)} km`;
  }
  return 'Above previous band';
};

const DeliverySettings = ({
  minOrderAmount = 0,
  deliveryChargeSlabs = [],
  canManage,
  onSaved,
}) => {
  const [minOrder, setMinOrder] = useState(String(minOrderAmount ?? 0));
  const [tiers, setTiers] = useState(() => slabsToTiers(deliveryChargeSlabs));
  const [initialSnapshot, setInitialSnapshot] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const nextMin = String(minOrderAmount ?? 0);
    const nextTiers = slabsToTiers(deliveryChargeSlabs);
    setMinOrder(nextMin);
    setTiers(nextTiers);
    setInitialSnapshot(serialize(nextMin, nextTiers));
  }, [minOrderAmount, deliveryChargeSlabs]);

  const isChanged = serialize(minOrder, tiers) !== initialSnapshot;

  const updateTier = (index, patch) => {
    setTiers((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addUptoSlab = () => {
    setTiers((prev) => {
      const next = [...prev];
      const aboveIndex = next.findIndex((t) => t.kind === 'above');
      const insertAt = aboveIndex >= 0 ? aboveIndex : next.length;
      const prevUpto = [...next].slice(0, insertAt).reverse().find((t) => t.kind === 'upto');
      const nextUpTo = prevUpto ? Number(prevUpto.upToKm) + 20 : 30;
      next.splice(insertAt, 0, { kind: 'upto', upToKm: nextUpTo, amount: 0 });
      if (!next.some((t) => t.kind === 'above')) {
        next.push({ kind: 'above', amount: 0 });
      }
      return next;
    });
  };

  const removeTier = (index) => {
    setTiers((prev) => {
      const row = prev[index];
      if (row?.kind === 'above') return prev;
      const next = prev.filter((_, i) => i !== index);
      if (!next.some((t) => t.kind === 'above')) {
        next.push({ kind: 'above', amount: 0 });
      }
      return next.length ? next : DEFAULT_TIERS;
    });
  };

  const handleSave = async () => {
    const minValue = Number(minOrder);
    if (!Number.isFinite(minValue) || minValue < 0) {
      toast.error('Min order must be 0 or greater');
      return;
    }

    const uptoRows = tiers.filter((t) => t.kind === 'upto');
    let lastUpTo = -1;
    for (const row of uptoRows) {
      const upToKm = Number(row.upToKm);
      const amount = Number(row.amount);
      if (!Number.isFinite(upToKm) || upToKm < 0) {
        toast.error('Each distance band needs a valid end km');
        return;
      }
      if (upToKm <= lastUpTo) {
        toast.error('Each band end km must be greater than the previous');
        return;
      }
      if (!Number.isFinite(amount) || amount < 0) {
        toast.error('Each slab needs a valid amount');
        return;
      }
      lastUpTo = upToKm;
    }

    const above = tiers.find((t) => t.kind === 'above');
    if (!above || !Number.isFinite(Number(above.amount)) || Number(above.amount) < 0) {
      toast.error('Above slab needs a valid amount');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        minOrderAmount: minValue,
        deliveryChargeSlabs: tiersToSlabs(tiers),
      };
      const response = await settingsAPI.updateDeliverySettings(payload);
      if (response.success) {
        toast.success('Delivery settings saved');
        const savedMin = response.data?.minOrderAmount ?? minValue;
        const savedTiers = slabsToTiers(response.data?.deliveryChargeSlabs || payload.deliveryChargeSlabs);
        setMinOrder(String(savedMin));
        setTiers(savedTiers);
        setInitialSnapshot(serialize(savedMin, savedTiers));
        onSaved?.(response.data);
      } else {
        toast.error(response.msg || 'Failed to save delivery settings');
      }
    } catch {
      toast.error('Failed to save delivery settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-card order-settings-card">
      <div className="settings-card-header">
        <div className="settings-card-title">
          <Truck size={20} />
          <div>
            <h2>Delivery &amp; min order</h2>
            <p>
              Min order amount and delivery charge by map radius (km). Uses coordinates distance, not road distance.
            </p>
          </div>
        </div>
        {canManage && (
          <button
            type="button"
            className="settings-save-btn"
            onClick={handleSave}
            disabled={!isChanged || saving}
          >
            <Save size={16} />
            {saving ? 'Saving...' : 'Save'}
          </button>
        )}
      </div>

      <div className="order-settings-body">
        <div className="delivery-settings-min-order">
          <label htmlFor="min-order-amount">Minimum order amount (QR)</label>
          <input
            id="min-order-amount"
            type="number"
            min="0"
            step="0.01"
            value={minOrder}
            disabled={!canManage}
            onChange={(e) => setMinOrder(e.target.value)}
          />
        </div>

        <div className="delivery-settings-slabs-header">
          <h3>Delivery charge slabs</h3>
          <p>Example: 0 – 30 km → 5 QR, 31 – 50 km → 10 QR, Above 50 km → 15 QR</p>
        </div>

        <div className="delivery-settings-slab-list">
          <div className="delivery-settings-slab-head delivery-settings-slab-head--simple">
            <span>Distance range</span>
            <span>Amount (QR)</span>
            <span />
          </div>
          {tiers.map((row, index) => {
            const startKm = rangeStartKm(tiers, index);
            return (
              <div
                key={`tier-${index}-${row.kind}`}
                className="delivery-settings-slab-row delivery-settings-slab-row--simple"
              >
                {row.kind === 'upto' ? (
                  <div className="delivery-settings-distance-cell">
                    <span className="delivery-settings-distance-label">{startKm}</span>
                    <span className="delivery-settings-distance-label">–</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={row.upToKm}
                      disabled={!canManage}
                      onChange={(e) => updateTier(index, { upToKm: e.target.value })}
                      aria-label={`Band ${index + 1} end km`}
                    />
                    <span className="delivery-settings-distance-label">km</span>
                  </div>
                ) : (
                  <div className="delivery-settings-distance-cell delivery-settings-distance-cell--above">
                    <span className="delivery-settings-above-badge">
                      {aboveThresholdLabel(tiers, index)}
                    </span>
                  </div>
                )}
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={row.amount}
                  disabled={!canManage}
                  onChange={(e) => updateTier(index, { amount: e.target.value })}
                  aria-label={`Band ${index + 1} amount`}
                />
                {canManage && row.kind === 'upto' ? (
                  <button
                    type="button"
                    className="order-settings-icon-btn"
                    onClick={() => removeTier(index)}
                    title="Remove band"
                  >
                    <Trash2 size={16} />
                  </button>
                ) : (
                  <span />
                )}
              </div>
            );
          })}
        </div>

        {canManage && (
          <div className="order-settings-add-row">
            <button type="button" className="settings-inline-btn" onClick={addUptoSlab}>
              <Plus size={16} />
              Add distance band
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliverySettings;
